// VERBATIM COPY of puttr-app/components/common/RolloutFunctions.js (synced 2026-09-11).
// The app, puttr-api src/rollout.js and puttr-ai rollout.js must all match; this web copy
// only DISPLAYS the pace (stored rollout preferred) and must be re-copied whenever the app file changes.
/*
 * Rollout pace metric: where the ball's center stopped (or would have
 * stopped) relative to the hole CENTER, in inches. Negative = short,
 * 0 = perfect (dies dead center), positive = past.
 *
 * Method (validated offline in puttr-api/scripts/ball_speed_analysis.js):
 * 1. Collapse duplicate path points. THE CLOCK IS THE CAMERA: one distinct
 *    position = one camera frame (stable hardware rate, fleet median 38 fps);
 *    the duplicates are irregular processing-loop re-reads, NOT time.
 * 2. Convert pixels to inches with the geometric scale curve (RHO_TABLE):
 *    all consoles share one physical geometry, anchored per console by its
 *    exactly-known hole size. The DETECTED ball radius is never used for
 *    distance (the blob shrinks with speed under motion blur).
 * 3. Build an along-path axis with the hole center at 0. Cut the approach
 *    when the ball's leading edge reaches the lip (topple/drop frames fake
 *    speed) or at rollback (short putts roll back down the ramp).
 * 4. Short putts report the directly observed stop (frame-rate free). Putts
 *    still moving at the cut are projected from their speed at the cut:
 *    wide misses rolling by the hole use the MEASURED remaining-travel curve
 *    for this mat (REMAINING_TABLE); captured makes use constant
 *    deceleration v^2 / 2a with the FIXED reference constant DEFAULT_ALPHA
 *    (see projectRollout for why the two differ). No per-putt or per-console
 *    deceleration fit feeds the projection any more: fleet-wide (40k putts,
 *    316 consoles, 2026-09-09) those fits are noisy (IQR +-25%), biased by
 *    outcome (makes fit ~30% softer than misses because reaching the hole
 *    selects for the least-decelerated paths), and one poisoned sample on a
 *    fresh install turned every later putt into "20 in past".
 */

export const BALL_D_IN = 1.68;   // standard golf ball diameter
export const HOLE_D_IN = 4.25;   // regulation hole diameter
// Uphill ramp deceleration for a rolling ball: (5/7) g sin(12deg) = 57.3 plus
// carpet rolling resistance. MEASURED at ~64 in/s^2 via the rollback method
// (scripts/rollback_fps.js: a stalled putt's uphill decel + downhill accel
// sum to pure gravity, so both fps and rolling resistance fall out per putt;
// measured rolling resistance ~7 in/s^2).
export const RAMP_DECEL_IN_S2 = 64;
// Fleet-median CAMERA frame rate measured across 562 consoles / 30 days by
// rollback_fps.js (geometric scale + camera-frame clock). The fleet is tight
// (median 38.6, IQR 36.6-40.7), so the default clock is a good approximation
// everywhere.
export const DEFAULT_FPS = 38;
// Default deceleration in tick units under the average frame rate.
export const DEFAULT_ALPHA = RAMP_DECEL_IN_S2 / (DEFAULT_FPS * DEFAULT_FPS);

export function median(arr) {
  if (!arr || !arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

// Human distance line: "4.2 in short" / "0.1 in past" / "1.8 ft past".
// "dead center" only when the value would round to 0.0 either side.
export function fmtRollout(v) {
  if (v == null || !Number.isFinite(v)) return null;
  const abs = Math.abs(v);
  if (abs < 0.05) return 'dead center';
  const side = v > 0 ? 'past' : 'short';
  return abs >= 24 ? `${(abs / 12).toFixed(1)} ft ${side}` : `${abs.toFixed(1)} in ${side}`;
}

// Speech-friendly rollout phrase for TTS: "4 inches past the hole".
export function spokenRollout(v) {
  if (v == null || !Number.isFinite(v)) return 'unreadable';
  if (Math.abs(v) < 0.5) return 'dead center';
  const inches = Math.round(Math.abs(v));
  const unit = inches === 1 ? 'inch' : 'inches';
  return `${inches} ${unit} ${v > 0 ? 'past the hole' : 'short of the hole'}`;
}

// Qualitative pace bucket from rollout error, mirroring the legacy
// getSpeedDescription vocabulary.
//
// FOR THE AI COACH ONLY. Player-facing surfaces (putt cards, game result
// panels, the pace stats screen) report the measured rollout and nothing
// else: where the ideal putt finishes — dying in the hole vs 3-6 inches
// past — is still an open coaching question, so the app does not tell a
// player their pace was wrong. These buckets exist so coachLogic can hand
// the model a coarse pace read; if the target moves, only the coach's
// language changes, not the numbers the player sees.
export function rolloutDescription(rollout, target = 0) {
  if (rollout == null) return 'PERFECT';
  const err = rollout - target;
  if (Math.abs(err) <= 3) return 'PERFECT';
  if (err > 9) return 'TOO FAST';
  if (err > 3) return 'LITTLE FAST';
  if (err < -9) return 'TOO SLOW';
  return 'LITTLE SLOW';
}

// Speed half (0-50) of the 100-point putt score: full credit at the target
// rollout (a putt dying at the hole), with an ASYMMETRIC falloff. Short is
// the cardinal sin (the ball never had a chance), so it keeps the harsh
// slope: zero at 12 inches short. Long is a lesser sin (the ball used the
// whole hole; the cost is lip-outs and comebackers), so it fades three times
// slower: zero at 36 inches past. A made putt can't have finished short, so
// a negative projected rollout on a make is measurement noise, not a pace
// fault — treat it as perfect.
export function speedScoreFromRollout(rollout, target = 0, made = false) {
  if (rollout == null) return 0;
  let err = rollout - target;
  if (made && err < 0) err = 0;
  const span = err < 0 ? 12 : 36;
  return Math.max(0, 50 * (1 - Math.abs(err) / span));
}

// Rollout-based version of GameFunctions.getPuttScore: entry half (0-50)
// unchanged, speed half from the rollout. Same 0-100 scale, so getPuttGrade
// bands keep working.
export function getPuttScoreRollout(angDiff, made, rollout, target = 0) {
  let entryScore = 50;
  const degOff = Math.abs(angDiff);
  if (degOff > 0) entryScore = entryScore * Math.max(0, 1 - degOff / 90);
  if (!made) entryScore = 0;
  return entryScore + speedScoreFromRollout(rollout, target, !!made);
}

// ── Geometric pixel scale along the ramp ──
// The DETECTED ball radius cannot be used as a ruler: the detection blob
// shrinks when the ball moves fast (the LED light smears and fewer pixels
// pass the white threshold), which faked high speeds. Instead, every console
// shares one physical geometry (camera a foot over the hole, 12 degree ramp),
// so the pixel scale is one fixed curve along the ramp, anchored per console
// by its exactly-known hole size. This table is rho = ball radius px / hole
// radius px versus u = distance-to-hole-center / hole radius, measured
// fleet-wide from ~62k SLOW (blur-free) samples across 350+ consoles over
// 30 days (puttr-api/scripts/scale_curve_fit.js).
const RHO_TABLE = [
  [1.00, 0.404], [1.25, 0.407], [1.50, 0.407], [1.75, 0.397],
  [2.00, 0.400], [2.25, 0.392], [2.50, 0.388], [2.75, 0.382],
  [3.00, 0.368], [3.25, 0.358], [3.50, 0.345], [3.75, 0.321],
  [4.00, 0.315], [4.25, 0.296], [4.50, 0.275], [4.75, 0.271], [5.00, 0.269],
];
const BALL_R_IN = BALL_D_IN / 2;

function rhoAt(u) {
  const t = RHO_TABLE;
  if (u <= t[0][0]) return t[0][1];
  for (let i = 1; i < t.length; i++) {
    if (u <= t[i][0]) {
      const u0 = t[i - 1][0], r0 = t[i - 1][1];
      return r0 + (t[i][1] - r0) * (u - u0) / (t[i][0] - u0);
    }
  }
  return t[t.length - 1][1];
}

// Local pixels-per-inch at a given pixel distance from the hole center.
export function pxPerInAt(dHolePx, holePxRad) {
  return (rhoAt(dHolePx / holePxRad) * holePxRad) / BALL_R_IN;
}

// Ball radius is ~0.4 hole radii, so the leading edge reaches the lip when
// the center is within 1.4 hole radii of the hole center.
const LIP_CUT = 1.4;

function fitLine(xs, ys) {
  const n = xs.length;
  if (n < 3) return null;
  let sx = 0, sy = 0, sxy = 0, sxx = 0;
  for (let i = 0; i < n; i++) { sx += xs[i]; sy += ys[i]; sxy += xs[i] * ys[i]; sxx += xs[i] * xs[i]; }
  const denom = n * sxx - sx * sx;
  if (!denom) return null;
  const B = (n * sxy - sx * sy) / denom;
  const A = (sy - B * sx) / n;
  const mean = sy / n;
  let ssRes = 0, ssTot = 0;
  for (let i = 0; i < n; i++) {
    const p = A + B * xs[i];
    ssRes += (ys[i] - p) ** 2;
    ssTot += (ys[i] - mean) ** 2;
  }
  return { A, B, r2: ssTot ? 1 - ssRes / ssTot : 0, n };
}

// One outlier-rejection pass (dropped camera frames appear as 2x velocities).
function robustFit(xs, ys) {
  let fit = fitLine(xs, ys);
  if (!fit || xs.length < 6) return fit;
  const residuals = xs.map((x, i) => ys[i] - (fit.A + fit.B * x));
  const sigma = Math.sqrt(residuals.reduce((a, r) => a + r * r, 0) / residuals.length);
  if (!sigma) return fit;
  const kx = [], ky = [];
  xs.forEach((x, i) => {
    if (Math.abs(residuals[i]) <= 2.5 * sigma) { kx.push(x); ky.push(ys[i]); }
  });
  if (kx.length >= 4 && kx.length < xs.length) {
    const refit = fitLine(kx, ky);
    if (refit) return refit;
  }
  return fit;
}

export function analyzeRollout(bp, hb) {
  if (!Array.isArray(bp) || bp.length < 3 || !Array.isArray(hb)) {
    return { ok: false, reason: 'no data' };
  }
  const hx = hb[0], hy = hb[1];
  const holePxRad = hb[2] / 2;

  // 1. Distinct samples; array index is the clock.
  const samples = [];
  for (let i = 0; i < bp.length; i++) {
    const x = bp[i][0], y = bp[i][1], r = bp[i][2];
    const prev = samples[samples.length - 1];
    if (prev && prev.x === x && prev.y === y && prev.rRaw === r) continue;
    if (!r || r < 4) continue; // partial blob / noise
    samples.push({ x, y, rRaw: r, tick: i });
  }
  if (samples.length < 5) return { ok: false, reason: 'too few samples', distinct: samples.length };

  // 2. Median-of-3 smoothing on radius (blob jitter).
  const rr = samples.map(s => s.rRaw);
  samples.forEach((s, i) => {
    const w = [rr[Math.max(0, i - 1)], rr[i], rr[Math.min(rr.length - 1, i + 1)]].sort((a, b) => a - b);
    s.r = w[1];
  });

  // 3. Positions: hole distance per sample, then arc length in inches using
  //    the geometric scale model (never the detected radius).
  samples.forEach((s) => { s.dHolePx = Math.hypot(s.x - hx, s.y - hy); });
  let L = 0;
  samples.forEach((s, i) => {
    if (i > 0) {
      const p = samples[i - 1];
      const dpx = Math.hypot(s.x - p.x, s.y - p.y);
      const pxPerIn = (pxPerInAt(s.dHolePx, holePxRad) + pxPerInAt(p.dHolePx, holePxRad)) / 2;
      L += dpx / pxPerIn;
    }
    s.L = L;
    s.dHoleIn = s.dHolePx / pxPerInAt(s.dHolePx, holePxRad);
  });

  // 4. Forward segment: cut at capture or at rollback.
  //    Capture cuts when the ball's LEADING EDGE reaches the lip (center
  //    within hole radius + ball radius). Beyond that the ball can already
  //    be toppling into the cup: it accelerates as it falls and its detected
  //    radius shrinks, both of which fake a huge speed. Only fully-supported
  //    rolling frames may count.
  //    Rollback is detected CUMULATIVELY: a short putt creeps back down the
  //    ramp at ~1px per sample, which a consecutive-sample check misses.
  let reached = false;
  let end = samples.length;
  let minYSeen = samples[0].y;
  for (let i = 1; i < samples.length; i++) {
    if (samples[i].dHolePx < holePxRad * LIP_CUT) { reached = true; end = i; break; }
    minYSeen = Math.min(minYSeen, samples[i - 1].y);
    if (samples[i].y > minYSeen + 3) { end = i; break; }
  }
  const seg = samples.slice(0, end);
  if (seg.length < 4) return { ok: false, reason: 'approach too short', distinct: samples.length, reached };

  // Post-cut rollback evidence. A reached putt that was NOT captured (lip
  // graze) stalls and rolls back down the ramp; those frames sit after the
  // cut and prove the ball came back out. Any post-cut sample back below
  // the hole and outside the lip zone (or fallen half a hole radius
  // down-view of the cut sample) means the putt cannot have finished past.
  // Without this, a short lip-graze miss was projected v^2/2a "past" (the
  // customer-reported wrong-sign bug; wrong-signed 80% of the time on this
  // putt shape in Monte Carlo validation, 2026-09-01).
  let rolledBack = false;
  if (reached) {
    const cutY = samples[end].y;
    for (let i = end + 1; i < samples.length; i++) {
      const s = samples[i];
      if (s.y > hy && (s.dHolePx > holePxRad * LIP_CUT || s.y > cutY + holePxRad * 0.5)) {
        rolledBack = true;
        break;
      }
    }
  }

  // High-water mark: the forward sample closest to the hole.
  let minIdx = 0;
  seg.forEach((s, i) => { if (s.dHolePx < seg[minIdx].dHolePx) minIdx = i; });

  // A wide miss that rolls PAST the hole keeps moving UP-view beyond its
  // closest approach. (Index distance alone is not enough: a short putt's
  // segment can include a couple of rollback-creep samples before the
  // cumulative cut fires, and those move DOWN-view.)
  const passed = !reached && seg[seg.length - 1].y < seg[minIdx].y - 2;

  // Hole center on the path axis. Before the hole its distance is remaining
  // approach; once abeam (passed), the hole sits AT the closest-approach
  // point and later positions are positive (past).
  let LHole;
  if (reached) {
    const cap = samples[end];
    LHole = cap.L + cap.dHoleIn;
  } else if (passed) {
    LHole = seg[minIdx].L;
  } else {
    LHole = seg[minIdx].L + seg[minIdx].dHoleIn;
  }

  // 5. Velocities between consecutive distinct samples. THE CLOCK IS THE
  //    CAMERA: one distinct position = one camera frame (a stable hardware
  //    rate), while the duplicate array entries are irregular loop re-reads
  //    of the same frame, NOT elapsed time. Dividing by the array-index gap
  //    would randomly halve velocities and wreck the fits.
  //    For short putts, stop at the high-water mark: the creep back down
  //    pollutes the tail.
  const fitSeg = (!reached && !passed) ? seg.slice(0, minIdx + 1) : seg;
  const vx = [], vy = [];
  for (let i = 1; i < fitSeg.length; i++) {
    const a = fitSeg[i - 1], b = fitSeg[i];
    const v = b.L - a.L; // inches per camera frame
    if (v <= 0) continue;
    vx.push(((a.L + b.L) / 2) - LHole);
    vy.push(v * v);
  }
  if (vx.length < 3) return { ok: false, reason: 'too few velocity points', distinct: samples.length, reached };

  // 6. v^2 = A + B*x -> stop where v^2 = 0.
  const fit = robustFit(vx, vy);
  if (!fit) return { ok: false, reason: 'fit failed', distinct: samples.length, reached };
  const decelOk = fit.B < 0;
  const fitStop = decelOk ? -fit.A / fit.B : null;

  // End state: where forward motion ended and how fast the ball still was.
  // Stopped-short putts end at the high-water mark with zero speed; captured
  // and passed-by putts are still moving and get projected from here.
  const stillMoving = reached || passed;
  const observedStop = (reached ? samples[end].L : (passed ? seg[seg.length - 1].L : seg[minIdx].L)) - LHole;
  const vLast = stillMoving ? median(vy.slice(-5).map(Math.sqrt)) : 0;
  const xLast = stillMoving ? vx[vx.length - 1] : observedStop;
  // Speed AT the cut: the v^2 fit evaluated at xLast. vLast (median of the
  // trailing 5 intervals) mixes in earlier, faster frames and lands a couple
  // of frames behind the cut, which understates lip speed on captured putts.
  const vFitSq = (fit.B < 0 && fit.r2 >= 0.5) ? fit.A + fit.B * xLast : null;
  const vCut = stillMoving && vFitSq != null && vFitSq > 0 ? Math.sqrt(vFitSq) : vLast;
  // Speed entering the visible ramp (in/tick); multiply by the console's fps
  // (or DEFAULT_FPS) for real units. The very first sample can be a partial
  // blob, so the median of the first few velocity points is used.
  const vEntry = median(vy.slice(0, 3).map(Math.sqrt));

  // 7. Diagnostics.
  const alpha = decelOk ? -fit.B / 2 : null;
  const impliedFps = alpha ? Math.sqrt(RAMP_DECEL_IN_S2 / alpha) : null;
  const nearest = samples.reduce((m, s) => (s.dHolePx < m.dHolePx ? s : m), samples[0]);
  const scaleRatio = hb[2] ? ((2 * nearest.r) / BALL_D_IN) / (hb[2] / HOLE_D_IN) : null;

  return {
    ok: true,
    reached,
    passed,
    rolledBack,
    fitStop,
    observedStop,
    vLast,
    vCut,
    xLast,
    vEntry,
    r2: fit.r2,
    decelOk,
    alpha,
    impliedFps,
    scaleRatio,
    distinct: samples.length,
    dupFactor: bp.length / samples.length,
    vPoints: vx.length,
    // Raw velocity intervals (hole-centered midpoint, v^2 in in/tick), kept
    // for the one-parameter stop estimator in projectRollout.
    xMids: vx,
    v2s: vy,
  };
}

// ── Start line ──
// The mat is flat, so the ball rolls a straight line from the tee. Fitting
// the ANGLE from the ~10 in visible segment is hopeless (±13 deg split-half
// noise), but the fitted lateral CROSSING at the hole line is rock solid
// (±0.05 in odd/even split over 7,300 fleet putts, per-tee medians within
// 0.4 in of zero, r=0.91 vs hole entry on makes). Anchoring the other end of
// the line at the known tee puts the angle on the full putt-length lever:
//   start line error = atan(crossing / distance), + = right of target.
// Validated 2026-07-15 (scratch startline_v2.mjs); ~91% of putts have the
// >= 6 clean pre-lip frames needed.
export function getStartLine(bp, hb, distanceFt) {
  if (!Array.isArray(bp) || bp.length < 3 || !Array.isArray(hb) || !hb[2]) {
    return { ok: false, reason: 'no data' };
  }
  const hx = hb[0], hy = hb[1], hpr = hb[2] / 2;

  // Distinct, fully-detected, pre-lip forward samples in hole-centered inches
  // (same cleaning rules as analyzeRollout).
  const pts = [];
  let minYSeen = Infinity;
  let prev = null;
  for (let i = 0; i < bp.length; i++) {
    const p = bp[i];
    if (!p || p.length < 3) continue;
    if (prev && prev[0] === p[0] && prev[1] === p[1] && prev[2] === p[2]) continue;
    prev = p;
    if (!p[2] || p[2] < 4) continue;
    const dHolePx = Math.hypot(p[0] - hx, p[1] - hy);
    if (dHolePx < hpr * LIP_CUT) break;                 // topple frames fake position
    if (pts.length && p[1] > minYSeen + 3) break;       // rollback creep
    minYSeen = Math.min(minYSeen, p[1]);
    const ppi = pxPerInAt(dHolePx, hpr);
    pts.push({ x: (p[0] - hx) / ppi, y: (p[1] - hy) / ppi });
  }
  if (pts.length < 6) return { ok: false, reason: 'too few samples', points: pts.length };

  // Least squares x = c + m*y: c is the lateral crossing at the hole line.
  const n = pts.length;
  let sy = 0, sx = 0, sxy = 0, syy = 0;
  for (const p of pts) { sy += p.y; sx += p.x; sxy += p.y * p.x; syy += p.y * p.y; }
  const denom = n * syy - sy * sy;
  if (!denom) return { ok: false, reason: 'degenerate fit', points: n };
  const m = (n * sxy - sy * sx) / denom;
  const crossingIn = (sx - m * sy) / n;

  const D = (distanceFt || 6) * 12;
  const deg = Math.atan(crossingIn / D) * (180 / Math.PI);
  return { ok: true, deg, crossingIn, points: n };
}

// "0.8 deg push" / "1.2 deg pull" / "on line" for a start-line read.
export function fmtStartLine(sl) {
  if (!sl || !sl.ok) return null;
  const abs = Math.abs(sl.deg);
  if (abs < 0.05) return 'on line';
  return `${abs.toFixed(1)}° ${sl.deg > 0 ? 'right' : 'left'}`;
}

// Measured remaining-travel curve: median actual remaining travel (inches)
// by speed at the cut (in/tick, median of the trailing 5 intervals, the same
// estimator projectRollout feeds it).
//
// Two independent fleet measurements agree on it:
//   - 0.1-1.2: 3,237 stopped-in-view putts / ~13k samples (2026-07-15),
//     held-out median error -0.1 to -0.4 in.
//   - 0.4-1.4: 5,480 wide misses that rolled PAST the hole (2026-09-09,
//     40k putts / 316 consoles / 21 days), Kaplan-Meier corrected for balls
//     that left the view still rolling. Those balls travel on the surface
//     beyond the hole line, i.e. exactly where a made putt's rollout is
//     projected. KM median: 0.8 -> 2.6, 0.9 -> 3.4, 1.0 -> 4.3, 1.1 -> 5.4,
//     1.2 -> 6.4, 1.3 -> 7.9, 1.4 -> 9.3 (the 1.3/1.4 points are heavily
//     censored and carry +-1 in).
// The constant-deceleration v^2/2a projection under the ramp physics
// (RAMP_DECEL_IN_S2 at DEFAULT_FPS) predicts 2.5x that travel (1.0 -> 11.3):
// the mat grabs a slowing ball far harder than a 12 deg ramp alone would.
// Above the table's top speed the curve continues as v^2/2a with the
// EFFECTIVE deceleration implied by the table's own slope (TAIL_ALPHA,
// ~0.10 in/tick^2), so the tail is continuous in value and slope.
const REMAINING_TABLE = [
  [0.1, 0.09], [0.2, 0.09], [0.3, 0.20], [0.4, 0.45], [0.5, 0.88],
  [0.6, 1.48], [0.7, 2.15], [0.8, 2.84], [0.9, 3.70], [1.0, 4.55],
  [1.1, 5.43], [1.2, 6.43], [1.3, 7.9], [1.4, 9.3],
];
const TAIL_ALPHA = 0.10;

export function remainingTravel(v) {
  const t = REMAINING_TABLE;
  const [vTop, remTop] = t[t.length - 1];
  if (!(v > 0)) return 0;
  if (v <= t[0][0]) return t[0][1] * (v / t[0][0]);
  for (let i = 1; i < t.length; i++) {
    if (v <= t[i][0]) {
      const v0 = t[i - 1][0], r0 = t[i - 1][1];
      return r0 + (t[i][1] - r0) * (v - v0) / (t[i][0] - v0);
    }
  }
  return remTop + (v * v - vTop * vTop) / (2 * TAIL_ALPHA);
}

// Final rollout in inches relative to the hole center: the ball's end-of-
// forward-motion position plus, if it was still moving, a projection from
// its speed at the cut. DETERMINISTIC: the same path always yields the same
// number on every device and on the server. Two moving regimes:
//   - PASSED (wide miss rolling by the hole): the ball really is on the mat
//     beyond the hole line, so the measured mat curve (REMAINING_TABLE)
//     applies. Validated against 5,480 such putts.
//   - CAPTURED (made): a counterfactual, since the ball dropped. The mat
//     curve says balls at capture speeds (0.6-1.3 in/tick) die within 0-4
//     in of the hole center, which would read "dead center" for nearly
//     every make and flatten the speed score (fleet p25-p75 would be -0.1
//     to 2.3 in). So makes are reported on a fixed REFERENCE scale instead:
//     constant deceleration DEFAULT_ALPHA (the 12 deg ramp physics at the
//     fleet frame rate), which spreads the capture band over ~0-15 in and
//     is the scale players and coaches have calibrated to since 2026-07-15.
//     It is a display convention, monotonic in lip speed, not a mat
//     measurement. Estimator: median over the approach intervals of
//     x + v^2/2a (each interval independently predicts the stop; far more
//     stable than extrapolating a two-parameter fit).
//
// `pooledAlpha` is accepted for call-site compatibility and IGNORED. The
// per-console deceleration pool (RolloutCalibration) and the per-putt v^2
// fit (when r2 >= 0.5) used to replace DEFAULT_ALPHA here; both were dropped
// on 2026-09-09 (see the header). Fleet-wide the per-putt fit moved 11% of
// makes off the fixed scale, a third of them by more than 5 in, in both
// directions (p10 -9.8 in, p90 +5.3 in).
//
// `made` is the game's own capture verdict (getMakeProbability > 1) when the
// caller has one; a putt the game scored as a miss must never take the
// captured-projection branch, whatever the frames look like.
export function projectRollout(analysis, pooledAlpha, made) {
  if (!analysis || !analysis.ok) return null;
  if (!analysis.vLast) return analysis.xLast; // ball stopped in view
  if (analysis.reached) {
    // Lip graze: the rollback frames (or the game's miss verdict) prove the
    // ball was never captured, so the observed high-water mark is the truth
    // and the result can never be past the hole center.
    if (analysis.rolledBack || made === false) {
      return Math.min(analysis.observedStop, 0);
    }
    if (analysis.xMids && analysis.xMids.length >= 3) {
      return median(analysis.xMids.map((x, i) => x + analysis.v2s[i] / (2 * DEFAULT_ALPHA)));
    }
    return analysis.xLast + (analysis.vLast * analysis.vLast) / (2 * DEFAULT_ALPHA);
  }
  return analysis.xLast + remainingTravel(analysis.vLast);
}

// ── Pace consistency ──
// Dispersion is measured on the ball's ARRIVAL SPEED, not on rollout inches.
// Rollout puts makes, wide misses and stopped-short putts on three different
// scales (reference physics, mat curve, observed stop), so a range across a
// mixed group counted the scale gaps as player inconsistency. Arrival speed
// is one axis for every outcome and is what the sensor measures directly.
//
// Signed, in in/tick (multiply by DEFAULT_FPS for in/s):
//   still moving at the cut (made, or rolled by)  -> vLast, the speed at the
//     last clean frames before the lip
//   stopped short in view                         -> minus the speed it
//     LACKED to reach the hole center, -sqrt(2 * DEFAULT_ALPHA * gap), so a
//     putt that died 4 in short sits below one that died 1 in short
export function arrivalSpeed(analysis) {
  if (!analysis || !analysis.ok) return null;
  if (analysis.vLast > 0) return analysis.vLast;
  const gap = Math.max(0, -analysis.xLast);
  return -Math.sqrt(2 * DEFAULT_ALPHA * gap);
}

// Linear-interpolated quantile, p in [0, 1].
export function quantile(arr, p) {
  if (!arr || !arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const pos = (s.length - 1) * p;
  const lo = Math.floor(pos), hi = Math.ceil(pos);
  return s[lo] + (s[hi] - s[lo]) * (pos - lo);
}

// Interquartile range: the spread of the middle half, immune to one wild
// putt in a way a max-minus-min range never is.
export function iqr(arr) {
  if (!arr || arr.length < 2) return 0;
  return quantile(arr, 0.75) - quantile(arr, 0.25);
}

// Consistency cutoffs on the arrival-speed IQR (in/tick), set from the fleet
// (40k putts / 316 consoles / 21 days, 2026-09-09, groups of one player at
// one distance and tee): the fleet IQR runs p10 0.10, p25 0.17, p50 0.29,
// p75 0.60, p90 0.95. "Very consistent" is roughly the best quarter of the
// fleet, "not consistent" roughly the worst third. Same-putt measurement
// noise is ~0.03 in/tick, so both cutoffs sit well above the sensor floor.
// In in/s at the fleet frame rate: 0.18 -> 6.8 in/s, 0.50 -> 19 in/s.
export const VERY_CONSISTENT_IQR = 0.18;
export const NOT_CONSISTENT_IQR = 0.50;
const MIN_PUTTS_FOR_CONSISTENCY = 3;

// 'very consistent' | 'consistent' | 'not consistent' from a group's signed
// arrival speeds. Fewer than three readable putts is not evidence either
// way, so it reports the neutral middle label.
export function paceConsistency(arrivals) {
  const v = (arrivals || []).filter(x => Number.isFinite(x));
  if (v.length < MIN_PUTTS_FOR_CONSISTENCY) return 'consistent';
  const spread = iqr(v);
  if (spread < VERY_CONSISTENT_IQR) return 'very consistent';
  if (spread >= NOT_CONSISTENT_IQR) return 'not consistent';
  return 'consistent';
}
